import { useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { http, errorMessage } from "@/lib/api";
import { isValidPostcode } from "@/lib/postcode";

export const AddressDialog = ({ open, onOpenChange, customer, onSave }) => {
  const [a, setA] = useState({ line1: "", line2: "", town: "", postcode: "", ...customer.address, instructions: customer.instructions || "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setA({ ...a, [k]: e.target.value });

  const save = async () => {
    setError("");
    if (a.line1.trim().length < 3 || a.town.trim().length < 2) return setError("Please enter your full address.");
    if (!isValidPostcode(a.postcode)) return setError("Please enter a full UK postcode.");
    setLoading(true);
    try {
      const { data } = await http.post("/postcode/check", { postcode: a.postcode });
      if (!data.eligible) return setError("Sorry, we're not delivering to that postcode just yet.");
      const { instructions, ...address } = a;
      await onSave({ address: { ...address, postcode: data.postcode }, instructions });
      onOpenChange(false);
      toast.success("Delivery details updated.");
    } catch (err) {
      setError(err.response ? errorMessage(err) : err.message);
    } finally {
      setLoading(false);
    }
  };

  const F = ({ k, label, ...rest }) => (
    <div className="space-y-1.5">
      <Label htmlFor={`addr-${k}`} className="text-sm text-[#F7F5F0]/80">{label}</Label>
      <Input id={`addr-${k}`} value={a[k]} onChange={set(k)} className="lc-input" data-testid={`address-${k}-input`} {...rest} />
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto border-[#2E2E2E] bg-[#111] sm:rounded-3xl" data-testid="address-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Delivery details</DialogTitle>
          <DialogDescription>Update your delivery address and instructions.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {F({ k: "line1", label: "Address line 1", autoComplete: "address-line1" })}
          {F({ k: "line2", label: "Address line 2 (optional)", autoComplete: "address-line2" })}
          <div className="grid grid-cols-2 gap-3">
            {F({ k: "town", label: "Town", autoComplete: "address-level2" })}
            {F({ k: "postcode", label: "Postcode", autoComplete: "postal-code", className: "lc-input uppercase" })}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="addr-instructions" className="text-sm text-[#F7F5F0]/80">Delivery instructions</Label>
            <Textarea id="addr-instructions" value={a.instructions} onChange={set("instructions")} maxLength={500} className="rounded-xl border-[#2E2E2E] bg-[#0f0f0f]" data-testid="address-instructions-input" />
          </div>
          {error && <p className="text-sm text-[#ff8a3d]" role="alert" data-testid="address-error">{error}</p>}
          <button onClick={save} disabled={loading} className="lc-btn-primary w-full" data-testid="address-save">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
